import { createContext, useContext } from 'react';
import { FileSystemStorageInfo } from '../../../FileSystem/FileSystemInterface';

export type PhotoSetStats = {
  numBytes: number;
  numPhotos: number;
};

export const mergePhotoSetStats = (stats1: PhotoSetStats, stats2: PhotoSetStats): PhotoSetStats => ({
  numBytes: stats1.numBytes + stats2.numBytes,
  numPhotos: stats1.numPhotos + stats2.numPhotos,
});

export type PhotoGalleryInfo = {
  numPhotos: number;
  numBytes: number;
  /** Photos in the doc-dir, awaiting upload. Not LRU-managed; only removed on upload or explicit erase. */
  unsyncedPhotos: PhotoSetStats;
  /** Everything in the FileCache (originals + scaled). LRU-evictable. */
  cachedPhotos: PhotoSetStats;
};

export type BatchDeleteResult = {
  numDeleted: number;
  bytesFreed: number;
};

export type PhotoGalleryMigrationInfo = {
  fileCountToMigrate: number;
  bytesToMigrate: number;
};

export type PhotoGalleryMigrationResult = {
  filesMigrated: number;
  bytesMigrated: number;
};

export const ORIGINAL_SIZE = 'original';

export type ScaledPhotoSize = 256 | 512 | 1024 | 2048;
/** Every scaled size the gallery caches, smallest first. */
export const SCALED_PHOTO_SIZES: readonly ScaledPhotoSize[] = [256, 512, 1024, 2048];
export type PhotoSize = ScaledPhotoSize | typeof ORIGINAL_SIZE;
export type BestPhotoSizeResult = {
  uri: string;
  size: PhotoSize;
};

export type CachePhotosOptions = {
  concurrency?: number;
};

export type BestPhotoSizeScanDirection = 'lowerResolution' | 'higherResolution' | 'both';

export interface PhotoGallery {
  /** Returns a platform-friendly URI for displaying the photo. Checks local unsynced → cache → downloads from server. */
  getPhotoUri: (photoId: string, size?: PhotoSize) => Promise<string | undefined>;

  /**
   * Best-effort local-only URI lookup. Never triggers a download.
   *
   * Resolution order:
   *   - exact requested size if cached
   *   - if size is undefined: original > 2048 > 1024 > 512 > 256
   *   - if size is a number: exact > largest-cached-smaller-size
   *
   * Lazily warms an in-memory size index per photoId on first call;
   * subsequent calls are an in-memory map lookup.
   */
  getBestLocallyAvailablePhoto: (photoId: string, size: PhotoSize, scanDirection?: BestPhotoSizeScanDirection) => Promise<BestPhotoSizeResult | undefined>;

  /**
   * Synchronous variant of getBestLocallyAvailablePhoto. Returns a URI only when the photo's
   * size index has already been resolved (warmed) and a URI has previously been
   * computed for the chosen size. Returns undefined otherwise — caller should
   * fall through to the async getBestLocallyAvailablePhoto.
   *
   * Use to avoid one-render-frame undefined when the index is already warm.
   */
  getBestLocallyAvailablePhotoSync: (photoId: string, size: PhotoSize, scanDirection?: BestPhotoSizeScanDirection) => BestPhotoSizeResult | undefined;

  /** Returns binary photo data, downloading if not available locally. */
  getPhotoData: (photoId: string) => Promise<Blob | undefined>;

  /** Store a new photo (unsynced) associated with a scan. */
  setPhoto: (scanId: string, photoId: string, data: Blob) => Promise<void>;

  /** Store a new photo from a file:// URI (unsynced) associated with a scan. */
  setPhotoFromUri: (scanId: string, photoId: string, uri: string) => Promise<void>;

  /** Upload a photo to the backend. scanId needed for server-side association. */
  uploadPhoto: (scanId: string, photoId: string, timeoutSeconds?: number, onProgress?: (progress: number) => void) => Promise<void>;

  /** Delete a photo (original + scaled) from all local storage. */
  deletePhoto: (photoId: string) => Promise<void>;

  /** Delete all photos from the cache (original and scaled). Safe to do. Does not delete unsynced photos.*/
  clearPhotoCache: () => Promise<BatchDeleteResult>;

  /** Delete all photos from the documentDirectory (unsynced). Will lose data. Only pull in emergencies! Does not delete cached photos. */
  dangerouslyEraseUnsyncedPhotos: () => Promise<BatchDeleteResult>;

  /** Delete unsynced photos older than SYNC_RECORD_MAX_AGE_MS. These are stuck and will never sync. */
  deleteExpiredUnsyncedPhotos: () => Promise<BatchDeleteResult>;

  /** Move a photo from documentDirectory to cacheDirectory, marking it as synced. */
  markPhotoSynced: (scanId: string, photoId: string) => Promise<void>;

  /** Returns true if the photo is in the flat cache (confirmed synced). */
  isPhotoSynced: (photoId: string) => Promise<boolean>;

  /** Returns true if the photo is in documentDirectory (unsynced). */
  isPhotoUnsynced: (photoId: string) => Promise<boolean>;

  /** Get information about the photo gallery status. */
  getPhotoGalleryInfo: () => Promise<PhotoGalleryInfo>;

  /** Get information about the disk storage status. */
  getPhotoGalleryStorageInfo: () => Promise<FileSystemStorageInfo>;

  /** Remove empty scan directories from documentDirectory. */
  cleanupEmptyScanDirectories: () => Promise<void>;

  /**
   * Free cache space. Deletes scaled photos first, then oldest originals.
   * Returns BatchDeleteResult.
   */
  freeCacheSpace: (bytesToFree: number) => Promise<BatchDeleteResult>;

  /**
   * Ensure at least `bytesNeeded` of free disk space is available.
   * Frees cache space if necessary. Call before camera capture.
   * Returns BatchDeleteResult (zeros if no cleanup needed).
   */
  ensureFreeSpace: (bytesNeeded: number) => Promise<BatchDeleteResult>;

  /**
   * Download and cache photos, 10 at a time.
   * Skips downloading photos already available locally.
   * Returns total count of photos now available locally (including already-cached).
   */
  cachePhotos: (photoIds: string[], size?: PhotoSize, onProgress?: (progress: number) => void, options?: CachePhotosOptions) => Promise<number>;

  /** Get the IDs of all unsynced photos for a scan. */
  getUnsyncedPhotoIdsForScan: (scanId: string) => Promise<string[]>;

  /**
   * Get the IDs of all unsynced photos for all scans.
   * @returns A map of scan ID to photo IDs.
   */
  getUnsyncedPhotoIdsByScanId: () => Promise<Map<string, string[]>>;

  /** Get the IDs of all unsynced scans. */
  getUnsyncedScanIds: () => Promise<string[]>;

  // ********************************************
  // Legacy → FileCache migration
  // ********************************************

  /**
   * Fast check for whether any cached photos still live in the legacy
   * pre-FileCache layout (separate original/ and scaled/ subdirectories of the
   * platform cache). Cheap — does not enumerate files, just probes the legacy
   * directories for existence. Safe to call on every app start.
   */
  needsMigration: () => Promise<boolean>;

  /**
   * Walk the legacy cache directories to count pending files and bytes.
   * O(n) in the number of legacy files, but stat-only (no reads). Use to drive
   * a progress UI; pair with {@link migrate}.
   */
  getMigrationInfo: () => Promise<PhotoGalleryMigrationInfo>;

  /**
   * Move all photos from the legacy cache layout into the FileCache by calling
   * `fileCache.addFile` for each. Removes the now-empty legacy directories on
   * completion. Idempotent — a no-op when {@link needsMigration} is false.
   *
   * @param onProgress  0..1 progress, called per file (same shape as cachePhotos).
   */
  migrate: (onProgress?: (progress: number) => void) => Promise<PhotoGalleryMigrationResult>;
}

export const PhotoGalleryContext = createContext<PhotoGallery | null>(null);
export const usePhotoGallery = () => {
  const gallery = useContext(PhotoGalleryContext);
  if (!gallery) throw new Error('usePhotoGallery must be used within a PhotoGalleryProvider');
  return gallery;
};
